/**
 * useDesignDoc — the pass-design working-copy + save machinery extracted from
 * PassDesignEditor (plan Task 4.1) so the panel editor (legacy) and the
 * direct-manipulation canvas (AppleStudio) share ONE save behaviour: debounced
 * auto-save, 422-inline-errors, isActive semantics, registered pre-advance save
 * (forces isActive: true — see `hostSave`). Every behaviour comment moved with
 * the code — do not strip them.
 *
 * Two behaviours beyond the verbatim extraction (both S3 quality-review fixes):
 *
 *   - FLUSH-ON-UNMOUNT: the debounce cleanup alone drops up to `autoSaveMs` of
 *     edits when the host unmounts (Típusváltás / browser Back); the unmount
 *     cleanup fires a best-effort `save(undefined)` when the working copy is
 *     dirty and a save would have been allowed (`!saveBlocked && !saving`).
 *   - STALE-ERROR RELEASE: server findings only ever change via a server
 *     response, so a 422's `severity:'error'` findings would deadlock the
 *     editor (save disabled ⇒ no response ⇒ errors never clear). An edit marks
 *     them stale — still rendered inline, but no longer blocking — so the next
 *     save round-trip revalidates against the server.
 */

import type { DeepPartial, PassDesign, PassDesignError } from '@insurwiz/wallet-core';
import { mergePassDesign } from '@insurwiz/wallet-core';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiClientError, putPassDesign } from '../api/client';
import { hasBlockingError } from '../lib/validationRouting';
import type { NotificationSettings, PassDesignDoc, PassTemplateKind } from '../types';
import type { AppleStyle } from '../walletStyles';

export interface UseDesignDocArgs {
  templateKind: PassTemplateKind;
  appleStyle: AppleStyle;
  /** The loaded doc (persisted row or serviceToDesign seed) for this style. */
  initial: PassDesignDoc;
  /**
   * When set (e.g. 800), the hook debounces a `save(undefined)` after this many
   * ms of no edits — the PassStudio auto-save (§3.3 path 1). The write is skipped
   * while `saveBlocked` (hasBlockingError) or `saving` holds. Unset ⇒ manual-save-only.
   */
  autoSaveMs?: number;
  /**
   * Fired with the server-returned doc after a successful save so the page can
   * reconcile its react-query cache (['passDesign', kind, style]). Without this
   * the cached GET-one doc diverges from what was just persisted, and a return
   * visit within `staleTime` resurrects the pre-save (seeded) state.
   */
  onSaved?: (saved: PassDesignDoc) => void;
}

export interface UseDesignDocResult {
  design: PassDesign;
  setDesign: (d: PassDesign | ((prev: PassDesign) => PassDesign)) => void;
  /** Push-editor working copy (spec 2026-08-31 Part B). Null ⇒ built-in plan. */
  notifications: NotificationSettings | null;
  /** Rides the SAME debounced save as the design — null resets to built-in. */
  setNotifications: (n: NotificationSettings | null) => void;
  /** The server's built-in plan (seeds the editor panel). */
  notificationDefaults: NotificationSettings;
  applyPatch: (patch: DeepPartial<PassDesign>) => void;
  errors: PassDesignError[];
  isActive: boolean;
  seeded: boolean;
  saving: boolean;
  saveBlocked: boolean;
  /** The last save attempt failed WITHOUT persisting (network/5xx) — honest
   *  status for the UI; cleared when the next save attempt starts. */
  saveFailed: boolean;
  /** The Mentés button / debounce path (isActive preserved via COALESCE). */
  save: () => void;
  toggleActive: () => void;
  /** The host's pre-advance save: persists isActive TRUE, resolves doc|null(422). */
  hostSave: () => Promise<PassDesignDoc | null>;
}

export function useDesignDoc({
  templateKind,
  appleStyle,
  initial,
  autoSaveMs,
  onSaved,
}: UseDesignDocArgs): UseDesignDocResult {
  const [design, setDesignState] = useState<PassDesign>(initial.design);
  // Push-editor settings ride the same working-copy + save lifecycle as the
  // design (one PUT carries both) — always sent, so the stored value
  // round-trips verbatim and an explicit null clears to built-in.
  const [notifications, setNotificationsState] = useState<NotificationSettings | null>(
    initial.notifications ?? null,
  );
  const [errors, setErrors] = useState<PassDesignError[]>(initial.warnings);
  // Server findings describe the design as of the LAST save round-trip. The
  // moment the operator edits again they go stale: they stay VISIBLE inline
  // (they guide the fix) but stop BLOCKING — otherwise a 422 would deadlock
  // the editor permanently (errors only change via a server response, and a
  // blocked save can never produce one). A stale error lets the next save
  // through; the server re-validates and either clears the findings (200) or
  // re-issues them fresh (422 ⇒ stale=false ⇒ blocking again until the next edit).
  const [errorsStale, setErrorsStale] = useState(false);
  const [isActive, setIsActive] = useState(initial.isActive);
  const [seeded, setSeeded] = useState(initial.seeded);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const saveBlocked = hasBlockingError(errors) && !errorsStale;

  // Every design write flows through here so it can mark the current server
  // findings stale (see `errorsStale` above) — both callback conventions:
  // Fields uses a whole-design replace setter (`setDesign`); the visual section
  // editors (Colors/Images/Barcode) emit a DeepPartial patch — apply it via
  // mergePassDesign so both coexist against one working copy.
  const setDesign = useCallback((d: PassDesign | ((prev: PassDesign) => PassDesign)): void => {
    setErrorsStale(true);
    setDesignState(d);
  }, []);
  const applyPatch = (patch: DeepPartial<PassDesign>): void =>
    setDesign((d) => mergePassDesign(d, patch));
  // Same stale-error release as setDesign: a 422 NOTIFICATIONS_INVALID blocks
  // the save, and only a server response can change the findings — so a
  // notifications edit must mark them stale or the editor deadlocks.
  const setNotifications = useCallback((n: NotificationSettings | null): void => {
    setErrorsStale(true);
    setNotificationsState(n);
  }, []);

  // The last design a save (debounced OR explicit) wrote. The auto-save effect
  // compares the working `design` against it, so EVERY save path must update it
  // — otherwise a manual Mentés / registered pre-advance save that flushes an
  // edit leaves the ref stale and the debounce re-PUTs the identical design.
  const lastQueuedDesignRef = useRef(initial.design);
  const lastQueuedNotificationsRef = useRef<NotificationSettings | null>(
    initial.notifications ?? null,
  );

  // Per-render mirror of the working copy: an async save continuation closes
  // over the design it PUT, so freshness checks (did the operator edit while
  // the request was in flight?) must read the CURRENT design through a ref.
  const designRef = useRef(design);
  designRef.current = design;
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  // Unmount guard: the flush-on-unmount save below intentionally resolves
  // after the hook is gone — its continuations must not setState.
  const mountedRef = useRef(true);

  // Resolves the server doc on a confirmed persist, null on a 422 (handled —
  // the errors render inline), and REJECTS on anything else (network/5xx).
  async function save(nextActive: boolean | undefined): Promise<PassDesignDoc | null> {
    // `save` closes over the same `design` it PUTs, so marking it here covers
    // the debounce, the Mentés button, the active-toggle and the registered
    // host save alike.
    lastQueuedDesignRef.current = design;
    lastQueuedNotificationsRef.current = notifications;
    // Capture the pre-save flags so a FAILED write can revert the optimistic
    // flips below — a rejected PUT persisted nothing, so leaving the flips in
    // place would show "Mentve" / a flipped toggle the server never stored.
    const prevIsActive = isActive;
    const prevSeeded = seeded;
    setSaving(true);
    setSaveFailed(false);
    // Optimistically flip the seeded→saved indicator (and, on a first save of a
    // seeded design, the "first saved design active by default" flag) so the UI
    // reflects the write immediately; the awaited server doc reconciles below.
    if (nextActive === undefined) {
      if (seeded) setIsActive(true);
      setSeeded(false);
    } else {
      setIsActive(nextActive);
    }
    try {
      const saved = await putPassDesign(templateKind, appleStyle, {
        design,
        notifications,
        ...(nextActive === undefined ? {} : { isActive: nextActive }),
      });
      if (mountedRef.current) {
        setErrors(saved.warnings);
        // The findings describe the design we just PUT — re-arm blocking only
        // if the operator has NOT edited while the request was in flight (a
        // mid-flight edit must keep the follow-up auto-save unblocked).
        setErrorsStale(
          designRef.current !== lastQueuedDesignRef.current ||
            notificationsRef.current !== lastQueuedNotificationsRef.current,
        );
        setIsActive(saved.isActive);
        setSeeded(saved.seeded);
      }
      // Reconcile the page's query cache with the persisted doc so a switch
      // away-and-back within `staleTime` sees the saved state, not the stale
      // pre-save (seeded) cache entry. Fires even from the unmount flush — the
      // page (still mounted behind the editor) must learn about the persist.
      onSaved?.(saved);
      return saved;
    } catch (e) {
      // Nothing persisted on ANY failure — revert the optimistic flips.
      if (mountedRef.current) {
        setIsActive(prevIsActive);
        setSeeded(prevSeeded);
      }
      if (e instanceof ApiClientError && e.status === 422 && e.body.errors) {
        if (mountedRef.current) {
          setErrors(e.body.errors);
          setErrorsStale(
            designRef.current !== lastQueuedDesignRef.current ||
              notificationsRef.current !== lastQueuedNotificationsRef.current,
          );
        }
        return null;
      }
      if (mountedRef.current) setSaveFailed(true);
      throw e;
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }

  // `save` is a plain async closure over the CURRENT `design` (not a React Query
  // mutate), so a mount-only registration would capture the initial seed and PUT
  // stale data when the host advances before the debounce fires. Keep a ref
  // updated to the latest `save` every render, and expose STABLE wrappers that
  // forward to the ref — the host always awaits the current-design save.
  //
  // `hostSave` persists `isActive: true` (NOT undefined): the host awaits this
  // only to ADVANCE to the generate step, which means "generate THIS design",
  // so the saved (partner, kind, style) design must be the active one the mint's
  // getActive (AND is_active=TRUE) resolves. Passing undefined here would COALESCE
  // to the existing flag — so a design the operator had toggled OFF via the "Aktív
  // design" toggle (save(!isActive)) would stay is_active=FALSE, getActive would
  // return null, and the mint would silently fall back to the DEFAULT view model,
  // yielding a different pass than the preview (studio GET/preview uses getOne,
  // which has no is_active filter, so the preview always shows the saved design).
  // The auto-save debounce stays save(undefined) → COALESCE, preserving the flag.
  const saveFnRef = useRef(save);
  saveFnRef.current = save;
  const hostSave = useCallback(() => saveFnRef.current(true), []);

  // Internal fire-and-forget paths swallow the non-422 rejection: the failure
  // is already surfaced as state (`saveFailed` + reverted flips), and an
  // unhandled rejection here would just crash the console. `hostSave` above
  // keeps REJECTING so the host can gate navigation / render its retry hint.
  function saveNow(): void {
    void save(undefined).catch(() => undefined);
  }
  function toggleActive(): void {
    void save(!isActive).catch(() => undefined);
  }

  // Debounced auto-save (§3.3 path 1). Re-arms only when the working `design`
  // differs from the last design ANY save wrote (`save` updates the ref itself)
  // — comparing against a ref (rather than a boolean dirty flag) so the
  // `saving` false→true→false round-trip of a completed save cannot re-arm an
  // endless save loop. Skips the initial mount (no edit yet) and any window
  // where saveBlocked/saving holds, so a severity:'error' design is never
  // auto-persisted. Reuses the same save(undefined) path as the Mentés button.
  // `save` is a per-render closure over `design`, intentionally not a dep —
  // the listed deps re-arm the timer.
  useEffect(() => {
    if (autoSaveMs === undefined) return;
    if (
      design === lastQueuedDesignRef.current &&
      notifications === lastQueuedNotificationsRef.current
    )
      return;
    if (saveBlocked || saving) return;
    const t = setTimeout(() => {
      // Re-check at fire time: an explicit save (Mentés / registered host save)
      // may have flushed this exact design after the timer armed, without a
      // deps change ever clearing the timeout (React can batch the `saving`
      // true→false round-trip into one commit). Without this guard the pending
      // timer re-PUTs the identical, already-persisted design.
      if (
        design === lastQueuedDesignRef.current &&
        notifications === lastQueuedNotificationsRef.current
      )
        return;
      void save(undefined).catch(() => undefined);
    }, autoSaveMs);
    return () => clearTimeout(t);
  }, [design, notifications, autoSaveMs, saveBlocked, saving]);

  // Best-effort flush of a pending debounced edit on unmount (plan Task 4.1):
  // the debounce cleanup alone would drop up to `autoSaveMs` of edits when the
  // host unmounts (Típusváltás / browser Back). Refreshed every render so the
  // cleanup sees the CURRENT dirtiness; fires only when the debounce itself
  // would have been allowed to (auto-save mode, dirty, not blocked, no save
  // already in flight — an in-flight save either carries this design or, on a
  // mid-flight edit, is a race we do not double-PUT into). Manual-save-only
  // mode (`autoSaveMs` unset) never auto-persists, on unmount included.
  const flushRef = useRef<(() => void) | null>(null);
  flushRef.current =
    autoSaveMs !== undefined &&
    (design !== lastQueuedDesignRef.current ||
      notifications !== lastQueuedNotificationsRef.current) &&
    !saveBlocked &&
    !saving
      ? () => void saveFnRef.current(undefined).catch(() => undefined)
      : null;
  useEffect(() => {
    // StrictMode dev double-invoke runs this cleanup once before the real
    // mount — re-arm the guard on every (re)run.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      flushRef.current?.();
    };
  }, []);

  return {
    design,
    setDesign,
    notifications,
    setNotifications,
    notificationDefaults: initial.notificationDefaults,
    applyPatch,
    errors,
    isActive,
    seeded,
    saving,
    saveBlocked,
    saveFailed,
    save: saveNow,
    toggleActive,
    hostSave,
  };
}
